import { useRef, useEffect } from 'react';
import useModalState from '@sdk/components/ui/modal/useModalState';
import { BillingInfoType } from '@sdk/account/api/AccountApi';
import Text16 from '@sdk/components/ui/typography/Text16';
import Text14 from '@sdk/components/ui/typography/Text14';
import Row from '@sdk/components/ui/spacing/Row';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import Modal from '@sdk/components/ui/modal/Modal';
import useAnalytics from '@sdk/analytics/useAnalytics';

import {
  BillingWarningContainer,
  BillingWarningCTA,
} from '@web/account/billingWarnings/billingWarnings.styles';
import {
  iconMap,
  titleColorMap,
  titleMap,
  descriptionMap,
  buttonTextMap,
  analyticsEvent,
} from '@web/account/billingWarnings/ExpiryWarningModal/expiryWarningModal.consts';
import { WarningType } from '@web/account/billingWarnings/ExpiryWarningModal/expiryWarningModal.types';

export type Props = {
  warningType: WarningType;
  billingInfo: BillingInfoType;
  onClose: (warningType: WarningType, shouldGoToBilling: boolean) => void;
};

const ExpiryWarningModal = ({ warningType, billingInfo, onClose }: Props) => {
  const config = useRef({ noDismiss: true }).current;
  const { isVisible, open, close } = useModalState();
  const { track } = useAnalytics();

  useEffect(() => {
    open();

    if (analyticsEvent[warningType]) {
      track(analyticsEvent[warningType]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isVisible) {
    return null;
  }

  return (
    <Modal
      fixed
      config={config}
      isVisible={isVisible}
      onDismiss={() => {
        onClose(warningType, false);
        close();
      }}
      closePositionTop={18}>
      <BillingWarningContainer alignItems="center">
        {iconMap[warningType]}
        <Row mTop={10}>
          <Text16 bold themeColor={titleColorMap[warningType]}>
            {titleMap[warningType]}
          </Text16>
        </Row>
        <Row mTop={10} mBottom={20}>
          <Text14 textAlign="center" themeColor={ThemeColorType.gray}>
            {descriptionMap[warningType](billingInfo.creditCard.lastFour)}
          </Text14>
        </Row>
        <BillingWarningCTA
          buttonType={ButtonType.primary}
          onClick={() => {
            onClose(warningType, true);
            close();
          }}>
          {buttonTextMap[warningType]}
        </BillingWarningCTA>
      </BillingWarningContainer>
    </Modal>
  );
};

export default ExpiryWarningModal;
